import { computed, ref, type Ref } from 'vue';
import dayjs from 'dayjs';

import { DateLib } from '@shared/lib';
import { FLEXIBLE_DATES_RANGE } from '@entities/flexible-dates';

function getBeforeDate(date: DateLib.DateProp, daysDifferenceAmount: number) {
  if (!date) {
    return date;
  }

  return DateLib.prettyDate(
    dayjs(date).subtract(daysDifferenceAmount, 'day'),
    'YYYY-MM-DD',
  );
}

function getAfterDate(date: DateLib.DateProp, daysDifferenceAmount: number) {
  if (!date) {
    return date;
  }

  return DateLib.prettyDate(
    dayjs(date).add(daysDifferenceAmount, 'day'),
    'YYYY-MM-DD',
  );
}

function useFlexibleDates(
  depDate: Ref<DateLib.DateProp>,
  arrDate: Ref<DateLib.DateProp>,
) {
  const isFlexibleDates = ref(false);

  const depFromDate = computed<DateLib.DateProp>(() => {
    if (isFlexibleDates.value) {
      return getBeforeDate(depDate.value, FLEXIBLE_DATES_RANGE);
    }

    return depDate.value;
  });

  const depToDate = computed<DateLib.DateProp>(() => {
    if (isFlexibleDates.value) {
      return getAfterDate(depDate.value, FLEXIBLE_DATES_RANGE);
    }

    return depDate.value;
  });

  const arrFromDate = computed<DateLib.DateProp>(() => {
    if (isFlexibleDates.value) {
      return getBeforeDate(arrDate.value, FLEXIBLE_DATES_RANGE);
    }
    return arrDate.value;
  });

  const arrToDate = computed<DateLib.DateProp>(() => {
    if (isFlexibleDates.value) {
      return getAfterDate(arrDate.value, FLEXIBLE_DATES_RANGE);
    }
    return arrDate.value;
  });

  function setIsFlexibleDates(isFlexible: boolean) {
    isFlexibleDates.value = isFlexible;
  }

  function getDate(fromDate: DateLib.DateProp) {
    if (isFlexibleDates.value) {
      return getAfterDate(fromDate, FLEXIBLE_DATES_RANGE);
    }

    return fromDate;
  }

  return {
    isFlexibleDates,
    depFromDate,
    depToDate,
    arrFromDate,
    arrToDate,

    setIsFlexibleDates,
    getDate,
  };
}

export { useFlexibleDates, getBeforeDate, getAfterDate };
